import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * Cart-specific quantity selector.
 *
 * Unlike a normal product quantity selector, the cart allows the
 * minus button to go from the minimum quantity to 0.
 *
 * Quantity 0 is already understood by component-cart-items.js
 * as "remove this line item".
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Cart uses the absolute variant maximum.
   *
   * @returns {number | null}
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max;
  }

  /**
   * In the cart the minus button stays enabled at quantity 1.
   * It is disabled only once quantity reaches 0.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    minusButton.disabled = value <= 0;

    plusButton.disabled =
      effectiveMax !== null &&
      value >= effectiveMax;
  }

  /**
   * Decrease cart quantity.
   *
   * Normal behavior:
   * 3 -> 2
   * 2 -> 1
   *
   * At the variant minimum:
   * 1 -> 0
   *
   * component-cart-items.js then treats quantity 0 as
   * a line-item removal.
   *
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();

    /*
     * Once we've reached the normal variant minimum,
     * the next minus click means "remove from cart".
     */
    if (value <= min) {
      quantityInput.value = '0';

      this.onQuantityChange();
      this.updateButtonStates();

      return;
    }

    /*
     * Above the minimum, retain Horizon's normal
     * quantity-step behavior.
     */
    this.updateQuantity(-1);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define(
    'cart-quantity-selector-component',
    CartQuantitySelectorComponent
  );
}